import { Dispatch, SetStateAction } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  ArrowUpRight as ArrowUpRightIcon,
  DataImportHub as DataImportHubIcon,
} from '@mint-lib/icons';
import {
  Button,
  Divider,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { ProgressCircular } from '@mint-lib/tw-components/patterns';

import { useKnowledgeBase } from '../../../hooks/knowledge-base/useKnowledgeBase.js';
import type { FileNode } from '../../../types/fileSystem.js';
import { CheckboxGroup, type CheckboxItem } from './CheckboxGroup.jsx';

type Props = {
  isDisabled: boolean;
  isOpen: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  files: Array<FileNode & CheckboxItem>;
  areFilesLoading: boolean;
  onItemChange: (fileName: string, checked: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
};

const DesktopFileSelectionContent = ({
  isDisabled,
  isOpen,
  onOpenChange,
  files,
  areFilesLoading,
  onItemChange,
  onSelectAll,
  onDeselectAll,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { toggle: toggleKnowledgeBase } = useKnowledgeBase();

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button icon variant="plain" disabled={isDisabled}>
          <DataImportHubIcon />
        </Button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="z-popover flex max-h-100 w-122.5 flex-col gap-4"
          data-interactive
          side="bottom"
        >
          <div className="flex justify-between px-3 pt-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <DataImportHubIcon width={20} height={20} />
                <p className={typographyVariants({ variant: 'h4' })}>
                  {t('Knowledge base')}
                </p>
              </div>
              <p
                className={typographyVariants({
                  variant: 'bodyShort01',
                  color: 'secondary',
                })}
              >
                {t('All selected items will be included in this request')}
              </p>
            </div>

            <Button
              icon
              variant="text"
              size="large"
              onClick={toggleKnowledgeBase}
            >
              <ArrowUpRightIcon />
            </Button>
          </div>
          <div className="px-3">
            <Divider />
          </div>
          {areFilesLoading ? (
            <div className="flex h-37.5 items-center justify-center">
              <ProgressCircular size="small" />
            </div>
          ) : (
            <div className="overflow-auto">
              <CheckboxGroup
                items={files}
                onItemChange={onItemChange}
                onSelectAll={onSelectAll}
                onDeselectAll={onDeselectAll}
              />
            </div>
          )}
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export { DesktopFileSelectionContent };
